import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  ExportDropdown,
  Hours,
  InternalClientTooltip,
  Percent,
  ProjectLink,
  RouteLink,
  Widget,
  YesNoFilter,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  ReportPeriodFilter,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTaskFilters,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort } from '~/utils';
import mimeTypes from '~/utils/mimeTypes';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Meter from '../components/Meter';
import { Metric, Metrics } from '../components/Metric';
import Report from '../components/Report';
import Table from '../components/table';
import useReportsSearchParamsConfig from '../hooks/useReportsSearchParamsConfig';
import ReportActions from '../ReportActions';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const Task = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

const NoTask = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
  color: ${colors.grey25};
`;

const Member = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
`;

const NoMember = styled.div`
  display: flex;
  align-items: center;
  margin-left: 2rem;
  color: ${colors.grey25};
`;

export default function TimeSummaryByProjectAndTaskAndMember({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const memberFilters = useMemberFilters();
  const taskFilters = useTaskFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    includeProjectsAndTasksWithoutTime: false,
    sort: new QuerySort('project.name', 'asc'),
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...roleFilters.filters,
    ...taskFilters.filters,
    ...memberFilters.filters,
    ...timeFilters.filters,
  });

  // Init and sync URL search params
  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: reportsSearchParamsConfig.period,
        includeProjectsAndTasksWithoutTime: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },
        sort: { default: new QuerySort('project.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ...memberFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        taskFilters,
        roleFilters,
        timeFilters,
      ],
    ),

    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      includeProjectsAndTasksWithoutTime: params.includeProjectsAndTasksWithoutTime ? 'true' : undefined,
      sort: params.sort,
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...memberFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, taskFilters, roleFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(_.omit(values, 'sort'));
    hideFilters();
  };

  const handleSort = ({ column, sort }) => {
    setQuery((state) => ({ ...state, status: 'sorting' }));
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ ...params, sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.pdf);
                    setIsOpen(false);
                  }}>
                  Export to PDF
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <ReportPeriodFilter value={params.period} onChange={({ value }) => handleApplyFilters({ period: value })} />

        <YesNoFilter
          icon="filter"
          value={params.includeProjectsAndTasksWithoutTime ? 'yes' : 'no'}
          placeholder="Include projects without time"
          onChange={({ target: { value } }) =>
            handleApplyFilters({ includeProjectsAndTasksWithoutTime: value === 'yes' })
          }
          style={{ maxWidth: 'fit-content' }}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />

        <TaskFiltersBar filters={params} onChange={handleApplyFilters} />

        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />

        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} onSort={handleSort} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params, onSort }) {
  const report = query.data;
  const auth = useAuth();

  const timeDetail = useTimeEntriesReportURL({ params });

  return (
    <>
      <Report.Summary>
        <Widget>
          <Meter>
            <Meter.Value
              label="Client Billable"
              value={report.totals.billable}
              color={colors.success}
              tooltip={
                <>
                  <Hours value={report.totals.billable} /> hours (
                  <Percent value={report.totals.billable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Client Non-Billable"
              value={report.totals.nonBillable}
              color={colors.danger}
              tooltip={
                <>
                  <Hours value={report.totals.nonBillable} /> hours (
                  <Percent value={report.totals.nonBillable / report.totals.total} />)
                </>
              }
            />
            <Meter.Value
              label="Internal"
              value={report.totals.internal}
              color={colors.warning}
              tooltip={
                <>
                  <Hours value={report.totals.internal} /> hours (
                  <Percent value={report.totals.internal / report.totals.total} />)
                </>
              }
            />
          </Meter>

          <Metrics>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'billable' })}>
                <Metric.Value>
                  <Hours value={report.totals.billable} />
                </Metric.Value>
                <Metric.Label>Client Billable</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'non_billable' })}>
                <Metric.Value>
                  <Hours value={report.totals.nonBillable} />
                </Metric.Value>
                <Metric.Label>Client Non-Billable</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: 'internal' })}>
                <Metric.Value>
                  <Hours value={report.totals.internal} />
                </Metric.Value>
                <Metric.Label>Internal</Metric.Label>
              </RouteLink>
            </Metric>
            <Metric>
              <RouteLink to={timeDetail({ timeBillableTypeId: ['billable', 'non_billable', 'internal'] })}>
                <Metric.Value>
                  <Hours value={report.totals.total} />
                </Metric.Value>
                <Metric.Label>Total</Metric.Label>
              </RouteLink>
            </Metric>
          </Metrics>
        </Widget>
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem" name="project.name" onSort={onSort} sort={params.sort}>
              Project/Client
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Client
              <br />
              Billable
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Client
              <br />
              Non-Billable
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Internal
            </Table.Column>
            <Table.Column width="10rem" align="right">
              Total
            </Table.Column>
            <Table.Column isVisible={auth.projects.viewBudgetHours} width="9rem" align="right">
              Budget
            </Table.Column>
            <Table.Column isVisible={auth.projects.viewBudgetHours} width="9rem" align="right">
              Budget Left
            </Table.Column>
            <Table.Column isVisible={auth.projects.viewBudgetHours} width="9rem" align="right">
              Percent
              <br />
              Used
            </Table.Column>
          </Table.Header>

          <Table.Body fade={query.status === 'sorting'}>
            {report.records.map((project) => {
              return (
                <React.Fragment key={project.id}>
                  <Table.GroupRow top="4.075rem">
                    <Table.Cell>
                      <ProjectInfo>
                        <p>
                          <ProjectLink project={project} />
                        </p>
                        <small>
                          <ClientLink client={project.client} />
                          {project.client.isInternal && <InternalClientTooltip />}
                        </small>
                      </ProjectInfo>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ projectId: project.id, timeBillableTypeId: 'billable' })}>
                          <Hours value={project.billableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ projectId: project.id, timeBillableTypeId: 'non_billable' })}>
                          <Hours value={project.nonBillableHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ projectId: project.id, timeBillableTypeId: 'internal' })}>
                          <Hours value={project.internalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>
                        <RouteLink to={timeDetail({ projectId: project.id })}>
                          <Hours value={project.totalHours} />
                        </RouteLink>
                      </strong>
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell />
                    <Table.Cell />
                  </Table.GroupRow>

                  {project.tasks.map((task) => (
                    <React.Fragment key={task.id ?? 'no-task'}>
                      <Table.Row>
                        <Table.Cell>{task.id ? <Task>{task.name}</Task> : <NoTask>{task.name}</NoTask>}</Table.Cell>
                        <Table.Cell>
                          <strong>
                            {task.id ? (
                              <RouteLink
                                to={timeDetail({
                                  projectId: project.id,
                                  projectTaskId: task.id,
                                  timeBillableTypeId: 'billable',
                                })}>
                                <Hours value={task.billableHours} />
                              </RouteLink>
                            ) : (
                              <Hours value={task.billableHours} />
                            )}
                          </strong>
                        </Table.Cell>
                        <Table.Cell>
                          <strong>
                            {task.id ? (
                              <RouteLink
                                to={timeDetail({
                                  projectId: project.id,
                                  projectTaskId: task.id,
                                  timeBillableTypeId: 'non_billable',
                                })}>
                                <Hours value={task.nonBillableHours} />
                              </RouteLink>
                            ) : (
                              <Hours value={task.nonBillableHours} />
                            )}
                          </strong>
                        </Table.Cell>
                        <Table.Cell>
                          <strong>
                            {task.id ? (
                              <RouteLink
                                to={timeDetail({
                                  projectId: project.id,
                                  projectTaskId: task.id,
                                  timeBillableTypeId: 'internal',
                                })}>
                                <Hours value={task.internalHours} />
                              </RouteLink>
                            ) : (
                              <Hours value={task.internalHours} />
                            )}
                          </strong>
                        </Table.Cell>
                        <Table.Cell>
                          <strong>
                            {task.id ? (
                              <RouteLink to={timeDetail({ projectId: project.id, projectTaskId: task.id })}>
                                <Hours value={task.totalHours} />
                              </RouteLink>
                            ) : (
                              <Hours value={task.totalHours} />
                            )}
                          </strong>
                        </Table.Cell>
                        <Table.Cell>
                          <strong>
                            <Hours value={task.budgetHours} />
                          </strong>
                        </Table.Cell>
                        <Table.Cell>
                          <strong>
                            <Hours value={task.budgetHoursLeft} />
                          </strong>
                        </Table.Cell>
                        <Table.Cell style={{ color: task.percentUsed > 1 ? colors.danger : colors.black }}>
                          <strong>
                            <Percent value={task.percentUsed} />
                          </strong>
                        </Table.Cell>
                      </Table.Row>

                      {task.members.map((member) => {
                        const link = task.id && member.id;
                        return (
                          <Table.Row key={member.id ?? 'no-member'}>
                            <Table.Cell>
                              {member.id ? <Member>{member.name}</Member> : <NoMember>{member.name}</NoMember>}
                            </Table.Cell>
                            <Table.Cell>
                              {link ? (
                                <RouteLink
                                  to={timeDetail({
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    memberId: member.id,
                                    timeBillableTypeId: 'billable',
                                  })}>
                                  <Hours value={member.billableHours} />
                                </RouteLink>
                              ) : (
                                <Hours value={member.billableHours} />
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {link ? (
                                <RouteLink
                                  to={timeDetail({
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    memberId: member.id,
                                    timeBillableTypeId: 'non_billable',
                                  })}>
                                  <Hours value={member.nonBillableHours} />
                                </RouteLink>
                              ) : (
                                <Hours value={member.nonBillableHours} />
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {link ? (
                                <RouteLink
                                  to={timeDetail({
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    memberId: member.id,
                                    timeBillableTypeId: 'internal',
                                  })}>
                                  <Hours value={member.internalHours} />
                                </RouteLink>
                              ) : (
                                <Hours value={member.internalHours} />
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {link ? (
                                <RouteLink
                                  to={timeDetail({
                                    projectId: project.id,
                                    projectTaskId: task.id,
                                    memberId: member.id,
                                  })}>
                                  <Hours value={member.totalHours} />
                                </RouteLink>
                              ) : (
                                <Hours value={member.totalHours} />
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Hours value={member.budgetHours} />
                            </Table.Cell>
                            <Table.Cell>
                              <Hours value={member.budgetHoursLeft} />
                            </Table.Cell>
                            <Table.Cell style={{ color: member.percentUsed > 1 ? colors.danger : colors.black }}>
                              <Percent value={member.percentUsed} />
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: 'billable' })}>
                  <Hours value={report.totals.billable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: 'non_billable' })}>
                  <Hours value={report.totals.nonBillable} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: 'internal' })}>
                  <Hours value={report.totals.internal} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell>
                <RouteLink to={timeDetail({ timeBillableTypeId: ['billable', 'non_billable', 'internal'] })}>
                  <Hours value={report.totals.total} />
                </RouteLink>
              </Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell></Table.Cell>
              <Table.Cell></Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status
            total={report.records.length}
            label="Project"
            isLoading={query.status !== 'ready'}
            partialResults={report.partialResults}
          />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        value={filters.includeProjectsAndTasksWithoutTime ? 'yes' : 'no'}
        placeholder="Include projects without time"
        onChange={({ target: { value } }) => handleFilter({ includeProjectsAndTasksWithoutTime: value === 'yes' })}
      />

      <ReportPeriodFilter
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}
